import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import {
  FolderKanban,
  FileText,
  Award,
  ArrowRight,
  MessageSquare,
  Bell,
} from "lucide-react";
import DashboardLayout from "layouts/DashboardLayout";
import { useAuth } from "contexts/AuthContext";
import { dossierService } from "services/dossier.service";
import { attestationsService } from "services/attestations.service";
import { documentsService } from "services/documents.service";
import { ROUTES } from "constants/routes";
import type { Dossier } from "types/dossier.types";
import {
  WelcomeBanner,
  StatsGrid,
  StatCard,
  AnimatedList,
  AnimatedItem,
  Badge,
  ProgressBar,
  EmptyState,
  SectionHeading,
  CardsGrid,
  DOSSIER_STATUS_BADGE,
  STATUS_LABELS,
} from "components/dashboard";

// ─── Styled ───────────────────────────────────────────────────────────────────

const DossierCard = styled(Link)`
  background: white;
  padding: 1.25rem;
  text-decoration: none;
  color: inherit;
  border: 1.5px solid #e5e7eb;
  display: block;
  transition:
    border-color 0.18s,
    box-shadow 0.18s;
  &:hover {
    border-color: #ff6c00;
    box-shadow: 0 4px 16px rgba(255, 108, 0, 0.12);
  }
`;
const DestTitle = styled.p`
  font-size: 1rem;
  font-weight: 800;
  color: #111;
  margin-bottom: 0.25rem;
`;
const DestMeta = styled.p`
  font-size: 0.72rem;
  color: #6b7280;
  font-weight: 500;
  margin-bottom: 0.6rem;
`;
const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 0.5rem;
  font-size: 0.7rem;
  font-weight: 700;
  color: #9ca3af;
`;
const ViewLink = styled.span`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: #ff6c00;
`;

// ─── Component ────────────────────────────────────────────────────────────────

const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const [dossiers, setDossiers] = useState<Dossier[]>([]);

  // Clés stables utilisées comme identifiants dans `stats`
  const statsItems: {
    key: string;
    label: string;
    icon: React.ReactNode;
    link: string;
    accent?: boolean;
  }[] = [
    {
      key: "dossiers",
      label: "Dossiers actifs",
      icon: <FolderKanban size={18} />,
      link: ROUTES.STUDENT_PROCEDURES,
    },
    {
      key: "documents",
      label: "Documents déposés",
      icon: <FileText size={18} />,
      link: ROUTES.STUDENT_DOCUMENTS,
    },
    {
      key: "messages",
      label: "Nouveaux messages",
      icon: <MessageSquare size={18} />,
      link: ROUTES.STUDENT_MESSAGES,
    },
    {
      key: "notifications",
      label: "Notifications",
      icon: <Bell size={18} />,
      link: ROUTES.STUDENT_NOTIFICATIONS,
    },
    {
      key: "attestations",
      label: "Attestations AVI",
      icon: <Award size={18} />,
      link: ROUTES.STUDENT_ATTESTATIONS,
      accent: true,
    },
  ];

  const [stats, setStats] = useState<Record<string, number>>({
    dossiers: 0,
    documents: 0,
    messages: 0,
    notifications: 0,
    attestations: 0,
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [d, docs, att] = await Promise.all([
          dossierService.list(),
          documentsService.getMyDocuments(),
          attestationsService.getMyAttestations(),
        ]);
        setDossiers(d);
        setStats({
          dossiers: d.length,
          documents: docs.length,
          messages: 0,
          notifications: 0,
          attestations: att.length,
        });
      } catch {
        /* silently fail */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const pct = (d: Dossier) => {
    const steps = d.steps ?? [];
    if (!steps.length) return 0;
    return Math.round(
      (steps.filter((s) => s.status === "completed").length / steps.length) *
        100,
    );
  };

  return (
    <DashboardLayout title="Tableau de bord">
      <WelcomeBanner name={user?.firstName} />

      <StatsGrid>
        {statsItems.map((item) => (
          <StatCard
            key={item.key}
            icon={item.icon}
            value={stats[item.key] ?? 0}
            label={item.label}
            link={item.link}
            accent={item.accent}
          />
        ))}
      </StatsGrid>

      <SectionHeading
        title="Mes dossiers"
        action={
          <Link
            to={ROUTES.STUDENT_PROCEDURES}
            style={{
              color: "#ff6c00",
              fontSize: "0.78rem",
              fontWeight: 700,
              textDecoration: "none",
            }}
          >
            Tout voir →
          </Link>
        }
      />

      {loading ? null : dossiers.length === 0 ? (
        <EmptyState
          icon={<FolderKanban size={40} />}
          title="Aucun dossier"
          description="Vous n'avez pas encore de dossier de candidature."
          action={
            <Link
              to={ROUTES.CONTACT}
              style={{ color: "#ff6c00", fontWeight: 700, fontSize: "0.82rem" }}
            >
              Nous contacter →
            </Link>
          }
        />
      ) : (
        <AnimatedList>
          <CardsGrid>
            {dossiers.slice(0, 4).map((d) => (
              <AnimatedItem key={d.id}>
                <DossierCard to={`${ROUTES.STUDENT_PROCEDURES}/${d.id}`}>
                  <DestTitle>{d.destination.name}</DestTitle>
                  <DestMeta>
                    {d.destination.flagEmoji} · {d.destination.currency}
                  </DestMeta>
                  <Badge
                    variant={
                      DOSSIER_STATUS_BADGE[
                        d.status as keyof typeof DOSSIER_STATUS_BADGE
                      ] ?? "neutral"
                    }
                  >
                    {STATUS_LABELS[d.status] ?? d.status}
                  </Badge>
                  <ProgressBar percent={pct(d)} />
                  <CardFooter>
                    <span>{pct(d)}% complété</span>
                    <ViewLink>
                      Voir <ArrowRight size={12} />
                    </ViewLink>
                  </CardFooter>
                </DossierCard>
              </AnimatedItem>
            ))}
          </CardsGrid>
        </AnimatedList>
      )}
    </DashboardLayout>
  );
};

export default DashboardPage;
